import {
  type SupportedLanguage,
  getCurrentLocale,
  useTranslation,
} from "@/lib/i18n";
import type { Duration, Locale } from "date-fns";
import {
  differenceInHours,
  formatDate,
  type formatDistance,
  formatDistanceStrict,
  formatDuration,
  intervalToDuration,
  isAfter,
  toDate,
} from "date-fns";
import { isString } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { useCommonTranslation } from "./useCommonTranslation";

export type Preset = {
  date: string;
  dateWeekday: string;
  yearMonth: string;
  yearMonthShort: string;
  monthDay: string;
  monthDayShort: string;
  monthDayWeekday: string;
  dateTime: string;
  dateTimeWeekday: string;
  dateTimeFull: string;
  dateTimeFullWeekday: string;
  time: string;
};

const presets: Record<SupportedLanguage, Preset> = {
  "ja-JP": {
    date: "yyyy年M月d日",
    dateWeekday: "yyyy年M月d日（eee）",
    yearMonth: "yyyy年M月",
    yearMonthShort: "yyyy年 M月",
    monthDay: "M月d日",
    monthDayShort: "MM/dd",
    monthDayWeekday: "M月d日（eee）",
    dateTime: "yyyy/MM/dd HH:mm",
    dateTimeWeekday: "yyyy/MM/dd（eee） HH:mm",
    dateTimeFull: "yyyy年M月d日 H時m分",
    dateTimeFullWeekday: "yyyy年M月d日（eee） H時m分",
    time: "HH:mm",
  },
  "en-US": {
    date: "MMM d, yyyy",
    dateWeekday: "eee, MMM d, yyyy",
    yearMonth: "MMMM yyyy",
    yearMonthShort: "MMM yyyy",
    monthDay: "MMMM d",
    monthDayShort: "MMM d",
    monthDayWeekday: "eee, MMMM d",
    dateTime: "MMM d, yyyy h:mm a",
    dateTimeWeekday: "eee, MMM d, yyyy h:mm a",
    dateTimeFull: "MMM d, yyyy h:mm a",
    dateTimeFullWeekday: "eee, MMM d, yyyy h:mm a",
    time: "h:mm a",
  },
  "id-ID": {
    date: "d MMM yyyy",
    dateWeekday: "eee, d MMM yyyy",
    yearMonth: "MMMM yyyy",
    yearMonthShort: "MMM yyyy",
    monthDay: "d MMMM",
    monthDayShort: "d MMM",
    monthDayWeekday: "eee, d MMMM",
    dateTime: "d MMM yyyy HH:mm",
    dateTimeWeekday: "eee, d MMM yyyy HH:mm",
    dateTimeFull: "d MMM yyyy HH:mm",
    dateTimeFullWeekday: "eee, d MMM yyyy HH:mm",
    time: "HH:mm",
  },
  "vn-VN": {
    date: "d MMM yyyy",
    dateWeekday: "eee, d MMM yyyy",
    yearMonth: "MMMM yyyy",
    yearMonthShort: "MMM yyyy",
    monthDay: "d MMMM",
    monthDayShort: "d MMM",
    monthDayWeekday: "eee, d MMMM",
    dateTime: "d MMM yyyy HH:mm",
    dateTimeWeekday: "eee, d MMM yyyy HH:mm",
    dateTimeFull: "d MMM yyyy HH:mm",
    dateTimeFullWeekday: "eee, d MMM yyyy HH:mm",
    time: "HH:mm",
  },
  "th-TH": {
    date: "d MMM yyyy",
    dateWeekday: "eee, d MMM yyyy",
    yearMonth: "MMMM yyyy",
    yearMonthShort: "MMM yyyy",
    monthDay: "d MMMM",
    monthDayShort: "d MMM",
    monthDayWeekday: "eee, d MMMM",
    dateTime: "d MMM yyyy HH:mm",
    dateTimeWeekday: "eee, d MMM yyyy HH:mm",
    dateTimeFull: "d MMM yyyy HH:mm",
    dateTimeFullWeekday: "eee, d MMM yyyy HH:mm",
    time: "HH:mm",
  },
  "zh-CN": {
    date: "yyyy年M月d日",
    dateWeekday: "yyyy年M月d日 eee",
    yearMonth: "yyyy年M月",
    yearMonthShort: "yyyy年M月",
    monthDay: "M月d日",
    monthDayShort: "MM/dd",
    monthDayWeekday: "M月d日 eee",
    dateTime: "yyyy年M月d日 HH:mm",
    dateTimeWeekday: "yyyy年M月d日 eee HH:mm",
    dateTimeFull: "yyyy年M月d日 HH:mm",
    dateTimeFullWeekday: "yyyy年M月d日 eee HH:mm",
    time: "HH:mm",
  },
};

/**
 * 日付をフォーマットするためのフック。
 * @returns フォーマットされた日付文字列
 */
export function useFormatDate() {
  const { i18n } = useTranslation();

  type Param = Parameters<typeof formatDate>;
  type PresetArg = { preset: keyof Preset };
  return useCallback(
    (
      date: Param[0] | null,
      formatStr?: Param[1] | PresetArg,
      options?: Param[2],
    ) => {
      if (!date) return "";

      // i18n.languageを使用してReactの再レンダリングと同期
      const currentLanguage = (i18n.language as SupportedLanguage) || "en-US";

      // プリセットが存在しない言語の場合はen-USを使用
      const languagePresets = presets[currentLanguage] || presets["en-US"];

      const formatString = isString(formatStr)
        ? formatStr
        : languagePresets[formatStr?.preset ?? "date"];

      // 現在の言語に基づいてlocaleを動的に取得
      const locale = getCurrentLocale();

      return formatDate(date, formatString, { locale, ...options });
    },
    [i18n.language], // i18n.languageを使用してReactの状態と同期
  );
}

export function useLocaleTimeFormat(key: keyof Preset) {
  const { i18n } = useTranslation();
  const currentLanguage = (i18n.language as SupportedLanguage) || "en-US";
  const languagePresets = presets[currentLanguage] || presets["en-US"];
  return languagePresets[key];
}

/**
 * 期間をフォーマットするためのフック。
 * @returns フォーマットされた期間文字列
 * @example
 * const formatDuration = useFormatDuration();
 * const duration = { hours: 1, minutes: 30 };
 * const formattedDuration = formatDuration(duration);
 * console.log(formattedDuration); // "1時間30分" (jaの場合)
 */
export function useFormatDuration() {
  const locale = getCurrentLocale();
  const formatter = useCallback<typeof formatDuration>(
    (duration: Duration, options?: Parameters<typeof formatDuration>[1]) =>
      formatDuration(duration, { locale, ...options }),
    [locale],
  );

  return formatter;
}

/**
 * 開始日から終了日までの期間を返す。durationより細かい単位で表示する。
 * ２４時間以内は１日前としてまとめる。その他はdate-fnsのformatDistanceを使用する。
 *
 * @see https://date-fns.org/v4.1.0/docs/formatDistance
 * @returns フォーマットされた期間文字列
 * @example
 * const formatDistance = useFormatDistance();
 * const formattedDistance = formatDistance({ validFrom: "2024-01-01", validTo: "2024-01-02" });
 * console.log(formattedDistance); // "1日前" (jaの場合)
 */
export function useFormatDistance() {
  const tc = useCommonTranslation();
  const { i18n } = useTranslation();

  // 現在の言語に基づいてlocaleを取得する
  const [currentLocale, setCurrentLocale] = useState<Locale>(() =>
    getCurrentLocale(),
  );

  useEffect(() => {
    const updateLocale = async () => {
      try {
        const { getDateLocale } = await import("@/lib/i18n/core/date-locale");
        const locale = await getDateLocale(i18n.language as SupportedLanguage);
        setCurrentLocale(locale);
      } catch (error) {
        console.error("Failed to update locale:", error);
      }
    };

    updateLocale();
  }, [i18n.language]);

  const distance = useCallback(
    (
      date: string | number | Date,
      baseDate: string | number | Date,
      options?: Parameters<typeof formatDistance>[2],
    ) => {
      const hoursDifference = Math.abs(
        differenceInHours(toDate(date), toDate(baseDate)),
      );
      if (hoursDifference < 24) {
        return tc("date.lessThanOneDay");
      }

      const distance = formatDistanceStrict(date, baseDate, {
        locale: currentLocale,
        ...options,
      });
      if (isAfter(toDate(date), toDate(baseDate))) {
        return tc("date.futureDistance", { value: distance });
      }
      return tc("date.pastDistance", { value: distance });
    },
    [tc, currentLocale], // currentLocaleを依存配列に追加
  );

  return distance;
}

/**
 * 開始日から終了日までの期間を計算する。
 * @param from 開始日
 * @param to 終了日
 * @returns 年、月、日
 */
export function calculateDuration(from: Date, to: Date) {
  const {
    years = 0,
    months = 0,
    days = 0,
  } = intervalToDuration({
    start: from,
    end: to,
  });

  return { years, months, days };
}
